import React, { useState } from 'react';
import clsx from 'clsx';
import Spacer from '../Spacer';
import { Icon, Icons } from 'components/Icon';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import { Loader } from 'components/Loader';
interface TextFieldProps {
  type?: 'text' | 'email' | 'number' | 'date' | 'password';
  onChange?: (e: React.ChangeEvent<any>) => void;
  onBlur?: (e: React.FocusEvent<any, Element>) => void;
  value: string | null;
  label?: string;
  placeholder: string;
  name: string;
  size?: 'half' | 'sm' | 'md' | 'lg' | 'xl';
  disabled?: boolean;
  error?: string;
  height?: number;
  width?: number;
  isRequired?: boolean;
  rightIcon?: Icons;
  leftIcon?: Icons;
  showPasswordToggler?: boolean;
  className?: any;
  hasStrengthIndicator?: boolean;
  totalStrengthConditions?: number;
  strengthConditionsFulfilled?: number;
  isLoading?: boolean;
  validateDate?: boolean;
  maxDate?: string;
  maxLength?: number;
}

const TextField: React.FC<TextFieldProps> = ({
  type = 'text',
  onChange = () => {},
  onBlur = () => {},
  value,
  label,
  placeholder,
  size,
  name,
  disabled = false,
  error,
  height,
  width,
  isRequired,
  rightIcon,
  leftIcon,
  showPasswordToggler,
  className,
  hasStrengthIndicator,
  totalStrengthConditions,
  strengthConditionsFulfilled,
  isLoading,
  validateDate = true,
  maxDate = '',
  maxLength = 100
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const computedRootStyle = () => {
    const style: { [key: string]: number | string } = {};

    if (width) {
      style.width = `${width}px`;
    }

    return style;
  };

  const computedInputStyle = () => {
    const style: { [key: string]: number | string } = {};

    if (height) {
      style.height = `${height}px`;
    }

    return style;
  };

  const determineAgeMin = () => {
    if (validateDate && type === 'date' && name.includes('dateOfBirth')) {
      const currentDate = new Date();
      const minDate = new Date(currentDate);
      minDate.setFullYear(currentDate.getFullYear() - 18);

      const formattedMinDate = minDate.toISOString().split('T')[0];
      return formattedMinDate;
    } else if (type === 'date' && maxDate) {
      return maxDate;
    }
  };

  return (
    <div
      className={clsx({
        'w-full flex flex-col': true,
        'w-1/2': size === 'half'
      })}
      style={computedRootStyle()}
    >
      {label && (
        <div className="relative mb-2 flex items-center justify-between">
          <span className="flex items-center">
            <label
              className="text-[12px] font-medium text-[#353535]"
              htmlFor={`grid-${name}`}
            >
              {label}
            </label>
            {isRequired && (
              <div className="mx-2 mb-2">
                <Spacer width={3} />
                <Icon name={Icons.Required} />
              </div>
            )}
          </span>
        </div>
      )}
      <div>
        <div className="relative">
          {leftIcon && (
            <>
              <Icon width={20} height={20} name={leftIcon} />{' '}
              <Spacer width={3} />
            </>
          )}

          <input
            type={showPassword ? 'text' : type}
            className={clsx({
              'block w-full': true,
              'bg-red-50 border border-red-500 text-red-900 placeholder:text-red-700 text-sm rounded-lg focus:ring-red-500 focus:border-red-500':
                error,
              'text-[#353535] focus:border-[#FF6606] focus:ring-[#FF6606]':
                true,
              'p-6': size === 'xl',
              'py-3.5': size === 'lg',
              'p-2.5 text-sm': size === 'md',
              'p-2 text-xs': size === 'sm',
              'bg-[#F2F2F2]': disabled,
              [className]: className
            })}
            placeholder={placeholder}
            value={value ?? ''}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            style={computedInputStyle()}
            autoComplete="false"
            min={type === 'date' ? '1900-01-01' : ''}
            max={type === 'date' ? determineAgeMin() : ''}
            maxLength={maxLength}
          />
          {isLoading && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <Loader type="Wait" height={20} width={20} />
            </button>
          )}
          {rightIcon && <Icon width={20} height={20} name={rightIcon} />}
          {showPasswordToggler && (
            <>
              <div
                className="flex flex-col items-center justify-center"
                role="button"
                tabIndex={-1}
                onKeyDown={() => setShowPassword(prevState => !prevState)}
                onClick={() => setShowPassword(prevState => !prevState)}
              >
                <span className="text-[10px] font-bold text-primary-900">
                  {showPassword ? 'HIDE' : 'SHOW'}
                </span>
                <div className="mt-[1px]">
                  {/* <Icon name={Icons.Lock} width={18} height={20} /> */}
                </div>
              </div>
            </>
          )}
          {hasStrengthIndicator &&
            totalStrengthConditions &&
            Boolean(strengthConditionsFulfilled) && (
              <PasswordStrengthIndicator
                totalConditions={totalStrengthConditions}
                conditionsFulfilled={strengthConditionsFulfilled || 0}
              />
            )}
        </div>
        {error && (
          <p className="mt-2 text-xs capitalize text-red-600">{error}</p>
        )}
      </div>
    </div>
  );
};

export default TextField;
